/**
 * Import/Export API Client for Sentient CRM
 * Handles CSV import/export operations for startup workflows
 */

import { baseClient } from './baseClient';

export interface ImportResult {
  message: string;
  imported_count: number;
  total_rows: number;
  errors: string[];
}

export interface ExportStatus {
  status: string;
  data_counts: {
    contacts: number;
    deals: number;
    accounts: number;
  };
  recent_activity: {
    contacts_last_7_days: number;
    deals_last_7_days: number;
  };
  supported_formats: string[];
  max_file_size: string;
  features: string[];
}

class ImportExportApi {
  private static instance: ImportExportApi;

  public static getInstance(): ImportExportApi {
    if (!ImportExportApi.instance) {
      ImportExportApi.instance = new ImportExportApi();
    }
    return ImportExportApi.instance;
  }

  /**
   * Export contacts to CSV
   */
  async exportContacts(accountId?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (accountId) {
      params.append('account_id', accountId);
    }

    const response = await baseClient.get<Blob>(
      `/import-export/export/contacts?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Export deals to CSV
   */
  async exportDeals(accountId?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (accountId) {
      params.append('account_id', accountId);
    }

    const response = await baseClient.get<Blob>(
      `/import-export/export/deals?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Export accounts to CSV
   */
  async exportAccounts(): Promise<Blob> {
    const response = await baseClient.get<Blob>(
      '/import-export/export/accounts',
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Export all data as ZIP file
   */
  async exportAllData(): Promise<Blob> {
    const response = await baseClient.get<Blob>(
      '/import-export/export/all',
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Import contacts from CSV file
   */
  async importContacts(file: File, accountId?: string): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (accountId) {
      params.append('account_id', accountId);
    }

    const response = await baseClient.post<ImportResult>(
      `/import-export/import/contacts?${params.toString()}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response;
  }

  /**
   * Get import template for specific entity type
   */
  async getImportTemplate(entityType: 'contacts' | 'deals' | 'accounts'): Promise<Blob> {
    const response = await baseClient.get<Blob>(
      `/import-export/templates/${entityType}`,
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Get import/export system status
   */
  async getStatus(): Promise<ExportStatus> {
    return baseClient.get<ExportStatus>('/import-export/status');
  }

  /**
   * Download file helper
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export contacts and download
   */
  async exportAndDownloadContacts(accountId?: string): Promise<void> {
    try {
      const blob = await this.exportContacts(accountId);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `contacts_export_${timestamp}.csv`;
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to export contacts:', error);
      throw new Error('Failed to export contacts. Please try again.');
    }
  }

  /**
   * Export deals and download
   */
  async exportAndDownloadDeals(accountId?: string): Promise<void> {
    try {
      const blob = await this.exportDeals(accountId);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `deals_export_${timestamp}.csv`;
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to export deals:', error);
      throw new Error('Failed to export deals. Please try again.');
    }
  }

  /**
   * Export accounts and download
   */
  async exportAndDownloadAccounts(): Promise<void> {
    try {
      const blob = await this.exportAccounts();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `accounts_export_${timestamp}.csv`;
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to export accounts:', error);
      throw new Error('Failed to export accounts. Please try again.');
    }
  }

  /**
   * Export all data and download
   */
  async exportAndDownloadAllData(): Promise<void> {
    try {
      const blob = await this.exportAllData();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `crm_export_${timestamp}.zip`;
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to export all data:', error);
      throw new Error('Failed to export data. Please try again.');
    }
  }

  /**
   * Download import template
   */
  async downloadImportTemplate(entityType: 'contacts' | 'deals' | 'accounts'): Promise<void> {
    try {
      const blob = await this.getImportTemplate(entityType);
      const filename = `${entityType}_import_template.csv`;
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Failed to download template:', error);
      throw new Error('Failed to download template. Please try again.');
    }
  }

  /**
   * Validate CSV file before import
   */
  validateCsvFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return { valid: false, error: 'File must be a CSV file (.csv extension)' };
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    // Check if file is empty
    if (file.size === 0) {
      return { valid: false, error: 'File cannot be empty' };
    }

    return { valid: true };
  }

  /**
   * Import contacts with validation
   */
  async importContactsWithValidation(file: File, accountId?: string): Promise<ImportResult> {
    // Validate file first
    const validation = this.validateCsvFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      return await this.importContacts(file, accountId);
    } catch (error) {
      console.error('Failed to import contacts:', error);
      throw new Error('Failed to import contacts. Please check your file format and try again.');
    }
  }
}

// Export singleton instance
export const importExportApi = ImportExportApi.getInstance();

// Export types for use in other components
export type { ImportResult, ExportStatus };
